/**
 * ActivityChart — 14-day activity bar chart (pure CSS, no chart library).
 */

import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useApi } from '@/hooks/use-api'
import { useDashboardFilters } from './use-dashboard-filters'

type DayData = {
  readonly date: string
  readonly count: number
}

type ActivityChartResponse = {
  readonly days: readonly DayData[]
  /** 'week' when the range is too long for one bar per day. */
  readonly bucket?: 'day' | 'week'
}

function getDayLabel(dateStr: string, bucket: 'day' | 'week', count: number): string {
  const d = new Date(dateStr + 'T12:00:00')
  // Two weeks of days fit weekday initials; longer ranges need dates.
  if (bucket === 'day' && count <= 14) return d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function ActivityChart() {
  const apiFetch = useApi()
  const { query, range } = useDashboardFilters()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'activity-chart', query],
    queryFn: async (): Promise<ActivityChartResponse> => {
      const res = await apiFetch<ActivityChartResponse>(`/api/leads/analytics/activity-chart${query}`)
      if (!res.success || !res.data) return { days: [] }
      return res.data
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })

  const days = data?.days ?? []
  const maxCount = Math.max(1, ...days.map((d) => d.count))
  const totalActions = days.reduce((sum, d) => sum + d.count, 0)
  const bucket = data?.bucket ?? 'day'
  // Without a start date the chart is the last 14 days whatever is picked.
  const title = range.from ? (bucket === 'week' ? 'Activity (by week)' : 'Activity') : 'Activity (14 days)'
  // Label every bar when they fit, else about seven evenly spaced labels.
  const labelEvery = Math.max(1, Math.ceil(days.length / 7))

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">{title}</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          <span className="text-xs text-muted-foreground">{totalActions} total actions</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-[3px] h-24">
          {days.map((day) => {
            const heightPct = maxCount > 0 ? (day.count / maxCount) * 100 : 0
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${bucket === 'week' ? 'Week of ' : ''}${day.date}: ${day.count} actions`}>
                <span className="text-[9px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">
                  {day.count}
                </span>
                <div
                  className="w-full rounded-t-sm bg-primary/70 hover:bg-primary transition-colors min-h-[2px]"
                  style={{ height: `${Math.max(2, heightPct)}%` }}
                />
              </div>
            )
          })}
        </div>
        <div className="flex gap-[3px] mt-1">
          {days.map((day, i) => (
            <div key={day.date} className="flex-1 text-center">
              {i % (days.length <= 14 ? 2 : labelEvery) === 0 && (
                <span className="text-[9px] text-muted-foreground whitespace-nowrap">{getDayLabel(day.date, bucket, days.length)}</span>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
